import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './SupportNudge.css';

const LAUNCHER_ID = 'aerochat-launcher';
const DWELL_MS = 7_000;
const DISMISS_KEY = 'aerochat_support_nudge_dismissed';
const ONBOARDING_PATH_PREFIX = '/getstarted/';

function readAnchorRect() {
  const el = document.getElementById(LAUNCHER_ID);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  if (!rect.width || !rect.height) return null;
  return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
}

function isDismissedThisSession() {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export default function SupportNudge() {
  const { t } = useTranslation('supportNudge');
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const rafRef = useRef(null);

  const eligible = location.pathname.startsWith(ONBOARDING_PATH_PREFIX);

  useEffect(() => {
    setVisible(false);
    if (!eligible) return undefined;
    if (isDismissedThisSession()) return undefined;

    const timer = setTimeout(() => {
      if (isDismissedThisSession()) return;
      const rect = readAnchorRect();
      if (!rect) return;
      setAnchor(rect);
      setVisible(true);
    }, DWELL_MS);

    return () => clearTimeout(timer);
  }, [eligible, location.pathname]);

  useEffect(() => {
    if (!visible || !eligible) return undefined;

    const tick = () => {
      const rect = readAnchorRect();
      if (rect) {
        setAnchor((prev) => {
          if (
            prev &&
            prev.left === rect.left &&
            prev.top === rect.top &&
            prev.width === rect.width &&
            prev.height === rect.height
          ) {
            return prev;
          }
          return rect;
        });
      }
      rafRef.current = window.requestAnimationFrame(tick);
    };
    rafRef.current = window.requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    };
  }, [visible, eligible]);

  if (!eligible || !visible || !anchor) return null;

  const ringSize = Math.max(anchor.width, anchor.height) + 24;
  const ringStyle = {
    left: anchor.left + anchor.width / 2 - ringSize / 2,
    top: anchor.top + anchor.height / 2 - ringSize / 2,
    width: ringSize,
    height: ringSize,
  };

  const launcherCenterX = anchor.left + anchor.width / 2;
  const placeAbove = anchor.top > 200;
  const onLeftHalf = launcherCenterX < window.innerWidth / 2;

  const bubbleStyle = placeAbove
    ? { left: launcherCenterX, top: anchor.top - 12, transform: 'translate(-50%, -100%)' }
    : { left: launcherCenterX, top: anchor.top + anchor.height + 12, transform: 'translateX(-50%)' };

  const bubbleClass = [
    'support-nudge__bubble',
    placeAbove ? 'support-nudge__bubble--above' : 'support-nudge__bubble--below',
    onLeftHalf ? 'support-nudge__bubble--left' : 'support-nudge__bubble--right',
  ].join(' ');

  const handleDismiss = (e) => {
    e.stopPropagation();
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setVisible(false);
  };

  const handleOpen = () => {
    const launcher = document.getElementById(LAUNCHER_ID);
    if (launcher) launcher.click();
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setVisible(false);
  };

  return createPortal(
    <div className="support-nudge" aria-live="polite">
      <div className="support-nudge__ring" style={ringStyle} aria-hidden="true" />
      <div
        className={bubbleClass}
        style={bubbleStyle}
        role="button"
        tabIndex={0}
        onClick={handleOpen}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleOpen(); }}
        aria-label={t('openAria')}
      >
        <span className="support-nudge__text">{t('message')}</span>
        <button
          type="button"
          className="support-nudge__close"
          onClick={handleDismiss}
          aria-label={t('closeAria')}
        >
          &times;
        </button>
      </div>
    </div>,
    document.body
  );
}
